import { useEffect, useState, useMemo, useCallback } from "react";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTenant } from "../../contexts/TenantContext";
import {
  fetchInstructorCourses,
  fetchInstructorStats,
  fetchSessionsForCourse,
  fetchSessionRequestsForCourse,
  fetchInstructorSubmissions,
  cancelCourseSubmission,
} from "../../api/apiClient";
import SessionRequestDialog from "../../components/SessionRequestDialog";
import ScheduleSessionsDialog from "../../components/ScheduleSessionsDialog";
import MyClassesView from "./myClasses/MyClassesView";
import ProposalsView from "./proposals/ProposalsView";

// ── Main page ─────────────────────────────────────────────────────────────────
export default function InstructorMyCoursesPage({ defaultTab = "classes" }) {
  const { user }    = useTenant();
  const navigate    = useNavigate();

  const [courses,     setCourses]     = useState([]);
  const [, setStats]       = useState(null);
  const [sessionsMap, setSessionsMap] = useState({});
  const [sessionRequestsMap, setSessionRequestsMap] = useState({});
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [listTab, setListTab] = useState("current");
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [requestDialogCourseId, setRequestDialogCourseId] = useState(null);

  // Tab is locked by defaultTab prop (no switching within page)

  // Proposal state
  const [submissions, setSubmissions] = useState([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [submissionsError, setSubmissionsError] = useState(null);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleProposal, setScheduleProposal] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    setLoading(true);
    Promise.all([
      fetchInstructorCourses(user.id, user.tenantId, listTab),
      fetchInstructorStats(user.id),
    ])
      .then(([c, s]) => {
        const list = Array.isArray(c) ? c : [];
        setCourses(list);
        setStats(s || {});
        return Promise.all(
          list.map((course) =>
            Promise.all([
              fetchSessionsForCourse(course.id).catch(() => []),
              fetchSessionRequestsForCourse(course.id).catch(() => []),
            ]).then(([sessions, requests]) => ({
              courseId: course.id,
              sessions: Array.isArray(sessions) ? sessions : [],
              requests: Array.isArray(requests) ? requests : [],
            }))
          )
        );
      })
      .then((results) => {
        const sessMap = {};
        const reqMap = {};
        results.forEach(({ courseId, sessions, requests }) => {
          sessMap[courseId] = sessions;
          reqMap[courseId] = requests;
        });
        setSessionsMap(sessMap);
        setSessionRequestsMap(reqMap);
      })
      .catch((err) => {
        console.error(err);
        setError("We couldn't load your courses right now. Please try again.");
      })
      .finally(() => setLoading(false));
  }, [user?.id, user?.tenantId, listTab]);

  const loadSubmissions = useCallback(async () => {
    if (!user?.id) return;
    setSubmissionsLoading(true);
    setSubmissionsError(null);
    try {
      const data = await fetchInstructorSubmissions(user.id);
      setSubmissions(Array.isArray(data) ? data : []);
    } catch {
      setSubmissionsError("Failed to load proposals.");
    } finally {
      setSubmissionsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  const handleCancelSubmission = useCallback(async (sub) => {
    setSubmissionsError(null);
    try {
      await cancelCourseSubmission(sub.id);
      await loadSubmissions();
    } catch {
      setSubmissionsError("Failed to cancel submission.");
    }
  }, [loadSubmissions]);

  const proposalActions = useMemo(() => ({
    onSubmitNew: () => navigate("/courses/submit"),
    onRefresh: loadSubmissions,
    onWithdraw: handleCancelSubmission,
    onRevise: () => navigate("/courses/submit"),
    onSchedule: (proposal) => {
      setScheduleProposal(proposal);
      setScheduleDialogOpen(true);
    },
  }), [navigate, loadSubmissions, handleCancelSubmission]);

  const classActions = {
    onOpenClass: (course) => navigate(`/course-hub/${course.id}`),
    onViewRoster: (course) => navigate(`/course-hub/${course.id}/attendance`),
    onSessionDetails: (course) => navigate(`/course-hub/${course.id}/sessions`),
    onTakeAttendance: (course, session) => navigate(`/course-hub/${course.id}/attendance/${session.id}`),
    onViewAttendance: (course, session) => navigate(`/course-hub/${course.id}/attendance/${session.id}`),
    onAddSession: listTab === "current"
      ? (course) => {
        setRequestDialogCourseId(course.id);
        setRequestDialogOpen(true);
      }
      : undefined,
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto" }}>

      {defaultTab === "classes" && (
        <>
          <MyClassesView
            courses={courses}
            sessionsMap={sessionsMap}
            requestsMap={sessionRequestsMap}
            listTab={listTab}
            onListTab={setListTab}
            loading={loading}
            error={error}
            actions={classActions}
          />

      <SessionRequestDialog
        open={requestDialogOpen}
        onClose={() => setRequestDialogOpen(false)}
        courseId={requestDialogCourseId}
        courseName={courses.find((c) => c.id === requestDialogCourseId)?.title}
        instructorId={user?.id}
        onSuccess={() => {
          if (!requestDialogCourseId) return;
          fetchSessionRequestsForCourse(requestDialogCourseId)
            .then((reqs) => setSessionRequestsMap((prev) => ({
              ...prev,
              [requestDialogCourseId]: Array.isArray(reqs) ? reqs : [],
            })))
            .catch(() => {});
        }}
      />
        </>
      )}

      {defaultTab === "proposals" && (
        <>
          <ProposalsView
            submissions={submissions}
            loading={submissionsLoading}
            error={submissionsError}
            onDismissError={() => setSubmissionsError(null)}
            actions={proposalActions}
          />
          <ScheduleSessionsDialog
            open={scheduleDialogOpen}
            onClose={() => setScheduleDialogOpen(false)}
            proposal={scheduleProposal}
            onSuccess={() => {
              loadSubmissions();
            }}
          />
        </>
      )}
    </Box>
  );
}
